import {
  ProfileTemplateFieldMode,
  type ProfileTemplateFieldPoliciesDto,
} from '@up2stay/up2promo-api-client';
import { normalizePolicyMode as normalizeGovernancePolicyMode } from '@up2stay/up2promo-commons';

export const PROFILE_TEMPLATE_FIELD_KEYS = [
  'visibility',
  'language',
  'leadsAutoShareWithWorkspace',
  'jobTitle',
  'company',
  'location',
  'showProfileImage',
  'showUp2promoBranding',
  'accentColor',
  'layout',
  'theme',
  'headerStyle',
  'headerImage',
  'buttonShape',
  'leadForm',
] as const;

export type ProfileTemplateFieldKey =
  (typeof PROFILE_TEMPLATE_FIELD_KEYS)[number];

/** UI + API: only locked (festgeschrieben) or free (individuell). */
export const TEMPLATE_POLICY_OPTIONS: ProfileTemplateFieldMode[] = [
  ProfileTemplateFieldMode.LOCKED,
  ProfileTemplateFieldMode.FREE,
];

export function parseFieldPolicies(
  raw: unknown,
): ProfileTemplateFieldPoliciesDto {
  if (!raw || typeof raw !== 'object') return {};
  return normalizeFieldPolicies(raw as ProfileTemplateFieldPoliciesDto);
}

/** Maps unknown policy strings to locked or free. */
export function normalizePolicyMode(
  mode?: ProfileTemplateFieldMode | string,
): ProfileTemplateFieldMode {
  return normalizeGovernancePolicyMode(mode) as ProfileTemplateFieldMode;
}

export function normalizeFieldPolicies(
  policies: ProfileTemplateFieldPoliciesDto,
): ProfileTemplateFieldPoliciesDto {
  const {
    styling: _styling,
    integrations: _integrations,
    ...rest
  } = policies as ProfileTemplateFieldPoliciesDto & {
    styling?: ProfileTemplateFieldMode;
    integrations?: ProfileTemplateFieldMode;
  };

  const normalized: ProfileTemplateFieldPoliciesDto = {};
  for (const key of PROFILE_TEMPLATE_FIELD_KEYS) {
    const value = (
      rest as Record<string, ProfileTemplateFieldMode | undefined>
    )[key];
    if (value !== undefined) {
      (normalized as Record<string, ProfileTemplateFieldMode>)[key] =
        normalizePolicyMode(value);
    }
  }
  return normalized;
}

export function getFieldMode(
  policies: ProfileTemplateFieldPoliciesDto,
  key: ProfileTemplateFieldKey,
): ProfileTemplateFieldMode {
  return normalizePolicyMode(
    (policies as Record<string, ProfileTemplateFieldMode | undefined>)[key],
  );
}

export function isFieldLocked(
  policies: ProfileTemplateFieldPoliciesDto,
  key: ProfileTemplateFieldKey,
): boolean {
  return getFieldMode(policies, key) === ProfileTemplateFieldMode.LOCKED;
}
